import ApplicationController from "application_controller.js";
import * as Turbo from "@hotwired/turbo";

export default class extends ApplicationController {

  debug = false;

  static targets = ["select"];

  static values = {
    skeleton: String,
    count: { type: Number, default: 12 },
  };

  connect() {
    this._onlineHandler = this.retryPendingFetch.bind(this);
    window.addEventListener("online", this._onlineHandler);
  }

  disconnect() {
    window.removeEventListener("online", this._onlineHandler);
  }

  openSelect(event) {
    event.preventDefault();
    if (this.hasSelectTarget && this.selectTarget.showPicker) {
      this.selectTarget.showPicker();
    }
  }

  applyFromSelect(event) {
    this.log_function("applyFromSelect");

    this.showSkeletonLoading();

    const value = event.target.value;
    const params = new URLSearchParams();

    //* Preserve ?sd= param for hosting platforms (Render, Hatchbox)
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has("sd")) {
      params.set("sd", currentParams.get("sd"));
    }

    if (value && value !== "all") {
      const [type, slug] = value.split(":");
      if (type === "event_category") {
        params.set("event_category", slug);
      } else if (type === "village") {
        params.set("village", slug);
      }
    }

    //* Update browser URL so refreshing preserves the current filter
    const browserUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    history.pushState({}, "", browserUrl);

    params.set("format", "turbo_stream");
    const url = `${this.element.action}.turbo_stream?${params}`;

    this.fetchTurboStream(url);
  }

  apply() {
    this.log_function("apply");

    this.showSkeletonLoading();

    //* Build URL from form data and fetch as turbo_stream (skips full page render)
    const formData = new FormData(this.element);
    const params = new URLSearchParams(formData);

    //* Preserve ?sd= param for hosting platforms (Render, Hatchbox)
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has("sd")) {
      params.set("sd", currentParams.get("sd"));
    }

    //* Update browser URL so refreshing preserves the current filter
    const browserParams = new URLSearchParams(params);
    if (browserParams.get("event_category") === "all") browserParams.delete("event_category");
    if (browserParams.get("village") === "all") browserParams.delete("village");
    const browserUrl = browserParams.toString()
      ? `${window.location.pathname}?${browserParams}`
      : window.location.pathname;
    history.pushState({}, "", browserUrl);

    params.set("format", "turbo_stream");
    const url = `${this.element.action}.turbo_stream?${params}`;

    this.fetchTurboStream(url);
  }

  // ─── Fetch with offline retry ──────────────────────────────────────────────

  fetchTurboStream(url) {
    this._pendingUrl = url;

    fetch(url, {
      headers: { Accept: "text/vnd.turbo-stream.html" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        this._pendingUrl = null;
        return Turbo.renderStreamMessage(html);
      })
      .catch((error) => {
        this.log_error_function("fetchTurboStream", error);
        if (!navigator.onLine) {
          this.dispatch("offline", { bubbles: true });
        }
        // _pendingUrl stays set so retryPendingFetch can pick it up
      });
  }

  retryPendingFetch() {
    if (!this._pendingUrl) return;
    this.log_function("retryPendingFetch");
    const url = this._pendingUrl;
    this._pendingUrl = null;

    // Small delay to let the connection stabilize
    setTimeout(() => this.fetchTurboStream(url), 1500);
  }

  // ─── Skeleton loading ──────────────────────────────────────────────────────

  showSkeletonLoading() {
    this.log_function("showSkeletonLoading");

    const eventsList = document.getElementById("events-list");
    if (eventsList) {
      eventsList.innerHTML = this.skeletonValue.repeat(this.countValue);
    }

    //* Disconnect infinite-scroll controller but keep element in DOM for turbo_stream.replace
    const sentinel = document.getElementById("events_scroll_sentinel");
    if (sentinel) {
      sentinel.innerHTML = "";
      const controllers = (sentinel.getAttribute("data-controller") || "").split(" ").filter(c => c !== "infinite-scroll").join(" ").trim();
      if (controllers) {
        sentinel.setAttribute("data-controller", controllers);
      } else {
        sentinel.removeAttribute("data-controller");
      }
    }
  }
}
